/**
 * Hook utilitaire pour tracker les redirections 301 et les 404 côté React.
 * Insertion fire-and-forget dans la table `seo_events` via edge function.
 * Déduplication par session (sessionStorage) pour ne pas spammer.
 *
 * Note : les 301 résolues par Vercel (vercel.json) ne passent pas par React
 * et NE SONT PAS trackées ici. Pour ces dernières, voir Google Search Console.
 */

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-seo-event`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const DEDUPE_KEY = "naxis_seo_tracked";

type EventType = "301" | "404";

const dedupe = (key: string): boolean => {
  try {
    const raw = sessionStorage.getItem(DEDUPE_KEY);
    const set: Record<string, true> = raw ? JSON.parse(raw) : {};
    if (set[key]) return false;
    set[key] = true;
    sessionStorage.setItem(DEDUPE_KEY, JSON.stringify(set));
    return true;
  } catch {
    return true;
  }
};

const send = (event_type: EventType, from_path: string, to_path?: string) => {
  if (!ENDPOINT || !ANON_KEY) return;
  const key = `${event_type}:${from_path}`;
  if (!dedupe(key)) return;

  const payload = JSON.stringify({
    event_type,
    from_path,
    to_path: to_path ?? null,
    referrer: typeof document !== "undefined" ? document.referrer.slice(0, 500) : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
  });

  // fire-and-forget
  try {
    fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: ANON_KEY,
        Authorization: `Bearer ${ANON_KEY}`,
      },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* noop */
  }
};

export const trackRedirect = (from: string, to: string) => send("301", from, to);
export const track404 = (from: string) => send("404", from);
